import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { Navigate } from 'react-router-dom'
import { dataPurgeApi } from '@/services/api'
import Card from '@/components/ui/Card'
import Loading from '@/components/ui/Loading'
import Button from '@/components/ui/Button'
import Alert from '@/components/ui/Alert'
import Modal from '@/components/ui/Modal'
import {
  Trash2,
  AlertTriangle,
  Info,
  CheckCircle,
  Database,
  Lock,
} from 'lucide-react'
import { formatNumber } from '@/utils/format'

export default function DataPurge() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  // Only Super Admins can access this page
  if (user?.user_type !== 'super_admin') {
    return <Navigate to="/dashboard" />
  }

  const [selectedRecordType, setSelectedRecordType] = useState('')
  const [purgeDate, setPurgeDate] = useState('')
  const [purgeTime, setPurgeTime] = useState('23:59:59')
  const [confirmationText, setConfirmationText] = useState('')
  const [previewData, setPreviewData] = useState<any>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showPasswordModal, setShowPasswordModal] = useState(true)
  const [purgePassword, setPurgePassword] = useState('')
  const [purgeToken, setPurgeToken] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [isVerified, setIsVerified] = useState(false)

  // Verify password mutation
  const verifyPasswordMutation = useMutation({
    mutationFn: (password: string) => dataPurgeApi.verifyPassword(password),
    onSuccess: (data) => {
      console.log('Password verified - full response:', data)
      console.log('data.data:', data.data)
      console.log('Token value:', data.data?.token)
      const token = data.data?.token || ''
      console.log('Setting purgeToken to:', token)
      setPurgeToken(token)
      setIsVerified(true)
      setShowPasswordModal(false)
      setSuccess('Access granted to data purge')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      console.error('Password verification failed:', err)
      setError(err.response?.data?.message || 'Incorrect password')
      setPurgePassword('')
    },
  })

  // Get available record types (only after verification)
  const { data: recordTypesData, isLoading: loadingRecordTypes } = useQuery({
    queryKey: ['data-purge-record-types'],
    queryFn: () => dataPurgeApi.getRecordTypes(),
    enabled: isVerified,
  })

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: ({ record_type, purge_date }: { record_type: string; purge_date: string }) => {
      console.log('Preview mutation called with:', { record_type, purge_date, purgeToken })
      return dataPurgeApi.preview(record_type, purge_date, purgeToken)
    },
    onSuccess: (data) => {
      console.log('Preview API response (full):', data)
      console.log('data.data:', data?.data)
      console.log('data structure:', JSON.stringify(data, null, 2))

      const previewInfo = data.data
      console.log('Extracted previewInfo:', previewInfo)
      if (previewInfo) {
        console.log('previewInfo.counts:', previewInfo.counts)
        console.log('Type of counts:', typeof previewInfo.counts)
        console.log('Is array?:', Array.isArray(previewInfo.counts))
      }

      setPreviewData(previewInfo)
      setShowPreview(true)
      console.log('State updated - showPreview:', true, 'previewData:', previewInfo)
      setSuccess('Preview generated successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (error: any) => {
      console.error('Preview error (full):', error)
      console.error('Error response:', error.response)
      console.error('Error message:', error.response?.data?.message)
      setError(error.response?.data?.message || 'Failed to generate preview')
    },
  })

  // Execute purge mutation
  const executeMutation = useMutation({
    mutationFn: ({ record_type, purge_date, confirmation }: { record_type: string; purge_date: string; confirmation: string }) =>
      dataPurgeApi.execute(record_type, purge_date, confirmation, purgeToken),
    onSuccess: (data) => {
      setSuccess(`Successfully purged ${data.data?.total_deleted || 0} records`)
      setTimeout(() => setSuccess(''), 5000)
      setPreviewData(null)
      setShowPreview(false)
      setSelectedRecordType('')
      setPurgeDate('')
      setConfirmationText('')
      queryClient.invalidateQueries({ queryKey: ['admin-statistics'] })
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Failed to execute purge')
    },
  })

  const handlePreview = () => {
    console.log('handlePreview called')
    console.log('selectedRecordType:', selectedRecordType)
    console.log('purgeDate:', purgeDate)
    console.log('purgeTime:', purgeTime)
    console.log('purgeToken:', purgeToken)

    if (!selectedRecordType || !purgeDate) {
      console.log('Validation failed - missing record type or date')
      setError('Please select record type and date')
      return
    }

    setError('')
    setSuccess('')
    const fullDateTime = `${purgeDate} ${purgeTime}`
    console.log('Calling preview mutation with fullDateTime:', fullDateTime)
    previewMutation.mutate({
      record_type: selectedRecordType,
      purge_date: fullDateTime,
    })
  }

  const handleExecute = () => {
    if (confirmationText !== 'DELETE') {
      setError('Please type DELETE to confirm')
      return
    }

    setError('')
    setSuccess('')
    const fullDateTime = `${purgeDate} ${purgeTime}`
    executeMutation.mutate({
      record_type: selectedRecordType,
      purge_date: fullDateTime,
      confirmation: confirmationText,
    })
  }

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!purgePassword) {
      setError('Please enter the data purge password')
      return
    }
    setError('')
    setIsVerifying(true)
    verifyPasswordMutation.mutate(purgePassword)
    setIsVerifying(false)
  }

  const recordTypes = recordTypesData?.data?.record_types || {}
  const selectedRecordTypeInfo = recordTypes[selectedRecordType]

  if (loadingRecordTypes) return <Loading />

  return (
    <div className="space-y-6">
      {/* Password Verification Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => {}}
        title="Data Purge Access"
        size="md"
      >
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <Lock className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-900">
              <p className="font-semibold mb-1">Security Verification Required</p>
              <p>
                This page allows permanent deletion of system data. Please enter the data purge password from your .env file to proceed.
              </p>
            </div>
          </div>

          {error && (
            <Alert type="error" message={error} onClose={() => setError('')} />
          )}

          <div>
            <label className="label">
              <Lock className="w-4 h-4 inline mr-2" />
              Data Purge Password
            </label>
            <input
              type="password"
              className="input"
              placeholder="Enter DATA_PURGE_PASSWORD"
              value={purgePassword}
              onChange={(e) => setPurgePassword(e.target.value)}
              autoFocus
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              This password is set in the DATA_PURGE_PASSWORD environment variable
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              onClick={() => window.history.back()}
              variant="secondary"
              disabled={isVerifying || verifyPasswordMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={isVerifying || verifyPasswordMutation.isPending || !purgePassword}
            >
              <Lock className="w-4 h-4 mr-2" />
              {isVerifying || verifyPasswordMutation.isPending ? 'Verifying...' : 'Verify & Continue'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Success/Error Messages */}
      {success && <Alert type="success" message={success} />}
      {error && <Alert type="error" message={error} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center space-x-3">
            <Database className="w-8 h-8 text-red-600" />
            <span>Data Purge</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Permanently delete old records from the system
          </p>
        </div>
      </div>

      {/* Warning Notice */}
      <Card>
        <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-red-900">Warning: Irreversible Action</h3>
            <p className="text-sm text-red-700 mt-1">
              Data purging is permanent and cannot be undone. Always ensure you have a recent database backup before proceeding.
              Associated files (images, documents) will also be deleted from storage.
            </p>
          </div>
        </div>
      </Card>

      {/* Purge Configuration */}
      <Card title="Configure Purge">
        <div className="space-y-6">
          {/* Record Type Selection */}
          <div>
            <label className="label">
              Record Type
            </label>
            <select
              className="input"
              value={selectedRecordType}
              onChange={(e) => {
                setSelectedRecordType(e.target.value)
                setShowPreview(false)
                setPreviewData(null)
                setConfirmationText('')
              }}
            >
              <option value="">Select record type...</option>
              {Object.entries(recordTypes).map(([key, info]: [string, any]) => (
                <option key={key} value={key}>
                  {info.label}
                </option>
              ))}
            </select>

            {selectedRecordTypeInfo && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-blue-900 font-medium">{selectedRecordTypeInfo.description}</p>
                    {selectedRecordTypeInfo.warning && (
                      <p className="text-blue-700 mt-1">
                        <strong>Note:</strong> {selectedRecordTypeInfo.warning}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Date Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">
                Purge Date
              </label>
              <input
                type="date"
                className="input"
                value={purgeDate}
                onChange={(e) => {
                  setPurgeDate(e.target.value)
                  setShowPreview(false)
                  setPreviewData(null)
                  setConfirmationText('')
                }}
                max={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-gray-500 mt-1">
                Records created before this date will be deleted
              </p>
            </div>

            <div>
              <label className="label">
                Time
              </label>
              <input
                type="time"
                className="input"
                value={purgeTime}
                onChange={(e) => {
                  setPurgeTime(e.target.value)
                  setShowPreview(false)
                  setPreviewData(null)
                  setConfirmationText('')
                }}
                step="1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Include records up to this time
              </p>
            </div>
          </div>

          {/* Preview Button */}
          <div>
            <Button
              onClick={handlePreview}
              disabled={!selectedRecordType || !purgeDate || previewMutation.isPending}
              variant="secondary"
              className="w-full"
            >
              {previewMutation.isPending ? 'Generating Preview...' : 'Preview Purge'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Preview Results */}
      {console.log('Render check - showPreview:', showPreview, 'previewData:', previewData, 'previewData.counts:', previewData?.counts)}
      {showPreview && previewData && (
        <Card title="Purge Preview">
          <div className="space-y-6">
            {console.log('Inside preview card render')}
            {!previewData.counts && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-900">Debug: counts is missing or undefined</p>
                <pre className="mt-2 text-xs">{JSON.stringify(previewData, null, 2)}</pre>
              </div>
            )}
            {previewData.counts && (
              <>
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-yellow-900">
                        The following records will be permanently deleted:
                      </h3>
                      <div className="mt-3 space-y-2">
                        {Object.entries(previewData.counts || {}).map(([table, count]: [string, any]) => (
                        <div key={table} className="flex justify-between items-center text-sm">
                          <span className="text-yellow-800 font-medium">
                            {table.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                          </span>
                          <span className="text-yellow-900 font-bold">
                            {formatNumber(count)} record{count !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-yellow-300">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-bold text-yellow-900">Total Records:</span>
                        <span className="text-lg font-bold text-yellow-900">
                          {formatNumber(Object.values(previewData.counts).reduce((sum: number, count: any) => sum + count, 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

                {/* Confirmation */}
                <div>
                  <label className="label">
                    Type <span className="font-bold text-red-600">DELETE</span> to confirm
                  </label>
                  <input
                    type="text"
                    className="input font-mono"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
                    placeholder="Type DELETE to confirm"
                  />
                </div>

                {/* Execute Button */}
                <div className="flex space-x-3">
                  <Button
                    onClick={handleExecute}
                    disabled={confirmationText !== 'DELETE' || executeMutation.isPending}
                    variant="danger"
                    className="flex-1"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {executeMutation.isPending ? 'Purging Data...' : 'Execute Purge'}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowPreview(false)
                      setPreviewData(null)
                      setConfirmationText('')
                    }}
                    variant="secondary"
                    disabled={executeMutation.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Info Section */}
      <Card title="Important Information">
        <div className="space-y-4 text-sm text-gray-700">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Database Cleanup</p>
              <p className="text-gray-600 mt-1">
                Purging old records helps maintain database performance and reduces storage costs.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Backup Before Purge</p>
              <p className="text-gray-600 mt-1">
                Always ensure you have a recent database backup before executing a purge operation.
                Use the MySQL backup feature to create backups on-demand.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Cascade Deletions</p>
              <p className="text-gray-600 mt-1">
                Some record types will automatically delete related records due to foreign key constraints.
                For example, deleting barters will also delete associated images, categories, and group memberships.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Audit Trail</p>
              <p className="text-gray-600 mt-1">
                All purge operations are logged in the audit log for compliance and tracking purposes.
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
